import { useEffect, useRef, useState } from 'react';
import { createLevelMeter, pickMime } from '../aiinterview/media';
import { roomAPI } from '../../lib/roomApi';
import { isCandidate } from './useDailyCall';

const CHUNK_MS = 20000;
const MIN_PEAK = 0.02;
const STANDBY_MS = 60000;

// Records one audio track in standalone ~20 s files and hands each non-silent one to onChunk.
function startChunks(track, onChunk) {
  const stream = new MediaStream([track]);
  const mime = pickMime(['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']);
  let meter = null;
  try { meter = createLevelMeter(stream); } catch (e) { meter = null; }
  let stopped = false;
  let rec = null;
  let timer = null;
  let peak = 0;
  const sample = setInterval(() => { if (meter) peak = Math.max(peak, meter.read()); }, 250);
  const cycle = () => {
    if (stopped || track.readyState !== 'live') return;
    const parts = [];
    const t0 = Date.now();
    peak = meter ? 0 : 1;
    rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    rec.ondataavailable = (e) => { if (e.data?.size) parts.push(e.data); };
    rec.onstop = () => {
      if (parts.length && peak >= MIN_PEAK) onChunk(new Blob(parts, { type: rec.mimeType || mime || 'audio/webm' }), t0);
      cycle();
    };
    rec.start();
    timer = setTimeout(() => { if (rec.state !== 'inactive') rec.stop(); }, CHUNK_MS);
  };
  cycle();
  return () => {
    stopped = true;
    clearTimeout(timer);
    clearInterval(sample);
    try { if (rec && rec.state !== 'inactive') rec.stop(); } catch (e) { /* already stopped */ }
    meter?.close();
  };
}

// Interviewer-side capture for the copilot: your own mic, plus the candidate's audio when they consented.
// The server grants the candidate stream to one interviewer at a time (lease), so it is never sent twice.
export function useCopilotCapture({ call, joined, enabled, bookingId, consent }) {
  const [status, setStatus] = useState({ self: 'off', candidate: 'off' });
  const self = useRef({ track: null, stop: null });
  const cand = useRef({ track: null, stop: null, pauseUntil: 0 });

  useEffect(() => {
    const upload = (role) => (blob, t0) => roomAPI.audio(bookingId, blob, role, t0).then(({ data }) => {
      if (role === 'candidate' && data.lease === false) {
        cand.current.pauseUntil = Date.now() + STANDBY_MS;
        cand.current.stop?.();
        cand.current.track = null;
        cand.current.stop = null;
      }
    }).catch(() => {});
    const reconcile = (ref, track, role) => {
      if (ref.current.track === track) return;
      ref.current.stop?.();
      ref.current.track = track;
      ref.current.stop = track ? startChunks(track, upload(role)) : null;
    };
    const stopAll = () => { reconcile(self, null, 'self'); reconcile(cand, null, 'candidate'); };
    if (!call || !joined || !enabled || typeof window.MediaRecorder === 'undefined') {
      stopAll();
      setStatus({ self: 'off', candidate: 'off' });
      return undefined;
    }
    const tick = () => {
      const ps = call.participants();
      const la = ps.local?.tracks?.audio;
      reconcile(self, la?.state === 'playable' ? la.persistentTrack : null, 'self');
      const cp = Object.values(ps).find((p) => !p.local && isCandidate(p));
      const ca = cp?.tracks?.audio;
      const paused = Date.now() < cand.current.pauseUntil;
      reconcile(cand, consent && !paused && ca?.state === 'playable' ? ca.persistentTrack : null, 'candidate');
      setStatus({
        self: la?.state === 'playable' ? 'on' : 'muted',
        candidate: !consent ? 'no_consent' : !cp ? 'waiting' : paused ? 'standby' : ca?.state === 'playable' ? 'on' : 'muted',
      });
    };
    tick();
    const iv = setInterval(tick, 2000);
    return () => { clearInterval(iv); stopAll(); };
  }, [call, joined, enabled, bookingId, consent]);

  return status;
}
